<template>
  <div v-if="isOpen" class="fixed inset-0 z-[100001] flex items-center justify-center p-4 bg-black/70"
    @click="handleClickOutside">
    <div
      class="relative flex flex-col w-full p-0 mx-auto overflow-hidden bg-white shadow-2xl max-w-4xl max-h-[90vh] rounded-xl"
      @click.stop>
      <!-- Header with title and close button -->
      <div class="flex items-center justify-between p-4 border-b bg-gray-50">
        <h3 class="text-lg font-semibold text-gray-800">{{ title }}</h3>
        <button type="button" aria-label="Close" @click="closeModal"
          class="text-gray-500 transition hover:text-gray-700">
          <svg xmlns="http://www.w3.org/2000/svg" class="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>
      </div>
      <div class="flex-1 p-6 overflow-y-auto">
        <slot>
          <div class="grid gap-6" :class="resolvedDosen.nip ? 'md:grid-cols-2' : 'grid-cols-1'">
            <!-- Left column: data lokal -->
            <div>
              <div class="flex flex-col gap-6 mb-6 md:flex-row">
                <!-- Full responsive image -->
                <div class="flex justify-center w-full md:w-1/3">
                  <img :src="getFotoUrl(dosendata.foto, getDosenNama())" alt="Foto Dosen"
                    class="w-full max-w-[200px] h-auto object-cover border rounded-lg shadow-lg" />
                </div>
                <!-- Dosen information -->
                <div class="flex flex-col justify-center w-full md:w-2/3">
                  <table class="min-w-full border-separate border-spacing-y-3">
                    <tbody>
                      <tr>
                        <td class="w-1/4 align-top">
                          <span class="font-medium text-gray-900">Nama</span>
                        </td>
                        <td class="w-3/4 px-3">
                          <span class="text-gray-700">{{
                            dosendata.nama || "-"
                          }}</span>
                        </td>
                      </tr>
                      <tr>
                        <td class="w-1/4 align-top">
                          <span class="font-medium text-gray-900">NIP</span>
                        </td>
                        <td class="w-3/4 px-3">
                          <span class="text-gray-700">{{
                            dosendata.nip || "-"
                          }}</span>
                        </td>
                      </tr>
                      <tr>
                        <td class="w-1/4 align-top">
                          <span class="font-medium text-gray-900">NIDN</span>
                        </td>
                        <td class="w-3/4 px-3">
                          <span class="text-gray-700">{{
                            dosendata.nidn || "-"
                          }}</span>
                        </td>
                      </tr>
                      <tr>
                        <td class="w-1/4 align-top">
                          <span class="font-medium text-gray-900">Email</span>
                        </td>
                        <td class="w-3/4 px-3">
                          <span class="text-gray-700">{{
                            dosendata.email || "-"
                          }}</span>
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
            <!-- Right column: Profil UNG (scraped from ung.ac.id by NIP) -->
            <div v-if="resolvedDosen.nip"
              class="pt-5 border-t border-gray-100 md:pt-0 md:border-t-0 md:border-l md:pl-6">
              <div class="flex items-center gap-2 mb-3">
                <span class="text-sm font-semibold text-gray-900">Profil</span>
                <svg v-if="ung.loading" class="w-4 h-4 ml-1 text-primary animate-spin"
                  xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                  <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                  <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"></path>
                </svg>
              </div>

              <table v-if="ung.data" class="min-w-full border-separate border-spacing-y-2">
                <tbody>
                  <tr v-if="ung.data.jabatan_fungsional">
                    <td class="w-1/3 align-top">
                      <span class="font-medium text-gray-900">Jabatan Fungsional</span>
                    </td>
                    <td class="w-2/3 px-3">
                      <span class="text-gray-700">{{ ung.data.jabatan_fungsional }}</span>
                    </td>
                  </tr>
                  <tr v-if="ung.data.fakultas">
                    <td class="w-1/3 align-top">
                      <span class="font-medium text-gray-900">Fakultas / Unit</span>
                    </td>
                    <td class="w-2/3 px-3">
                      <span class="text-gray-700">{{ ung.data.fakultas }}</span>
                    </td>
                  </tr>
                  <tr v-if="ung.data.prodi">
                    <td class="w-1/3 align-top">
                      <span class="font-medium text-gray-900">Program Studi</span>
                    </td>
                    <td class="w-2/3 px-3">
                      <span class="text-gray-700">{{ ung.data.prodi }}</span>
                    </td>
                  </tr>
                  <tr v-if="ung.data.pendidikan && ung.data.pendidikan.length">
                    <td class="w-1/3 align-top">
                      <span class="font-medium text-gray-900">Riwayat Pendidikan</span>
                    </td>
                    <td class="w-2/3 px-3">
                      <ul class="pl-5 m-0 text-gray-700 list-disc">
                        <li v-for="(edu, i) in ung.data.pendidikan" :key="i" class="mb-1">
                          <span class="font-medium">{{ edu.jenjang }}</span> — {{ edu.instansi }}
                        </li>
                      </ul>
                    </td>
                  </tr>
                </tbody>
              </table>

              <p v-else-if="!ung.loading" class="text-sm text-gray-400">
                Data profil UNG tidak ditemukan untuk NIP ini.
              </p>
            </div>
          </div>

          <!-- Close button at bottom -->
          <div class="flex justify-end mt-6">
            <button type="button" @click="closeModal"
              class="px-4 py-2 text-gray-800 transition-colors bg-gray-200 rounded-md hover:bg-gray-300">
              Tutup
            </button>
          </div>
        </slot>
      </div>
    </div>
  </div>
</template>

<script setup>
import { defineProps, defineEmits, watch, ref, reactive, computed } from "vue";

const props = defineProps({
  isOpen: {
    type: Boolean,
    required: true,
  },
  title: {
    type: String,
    required: true,
  },
  dosendata: {
    type: [Array, Object],
    required: true,
  },
});

const emit = defineEmits(["update:isOpen"]);

// Close modal function
const closeModal = () => {
  emit("update:isOpen", false);
};

// Handle clicking outside the modal to close it
const handleClickOutside = (event) => {
  if (event.target === event.currentTarget) {
    closeModal();
  }
};
function getFotoUrl(foto, nama = "User") {
  if (!foto) {
    const initials = nama
      .split(" ")
      .map((n) => n[0])
      .join("")
      .substring(0, 2);
    return `https://ui-avatars.com/api/?name=${encodeURIComponent(
      initials
    )}&background=e5e7eb&color=6b7280&size=128`;
  }
  if (/^https?:\/\//.test(foto) || foto.startsWith("/storage")) {
    return foto;
  }
  return "/storage/" + foto;
}

// Helper function to get dosen name
const getDosenNama = () => {
  let dosen = Array.isArray(props.dosendata)
    ? props.dosendata[0]
    : props.dosendata;
  dosen = dosen || {};
  return dosen.nama || "User";
};
const pendidikanList = computed(() => {
  // Jika dosendata adalah array (misal dari v-for), ambil index 0
  let dosen = Array.isArray(props.dosendata)
    ? props.dosendata[0]
    : props.dosendata;
  dosen = dosen || {};
  const raw = dosen.pendidikan || "";
  let text = raw
    .replace(/<li>/g, "\n")
    .replace(/<[^>]+>/g, "")
    .split(/\n|<br\s*\/?>(?=<)/i)
    .map((s) => s.trim())
    .filter((s) => s.length > 0);
  return text;
});

const jabAkademikText = computed(() => {
  let dosen = Array.isArray(props.dosendata)
    ? props.dosendata[0]
    : props.dosendata;
  dosen = dosen || {};
  const raw = dosen.jabakademik || "";
  return raw.replace(/<[^>]+>/g, "").trim();
});

// Resolved dosen object (dosendata may be an array or object).
const resolvedDosen = computed(() =>
  Array.isArray(props.dosendata)
    ? props.dosendata[0] || {}
    : props.dosendata || {}
);

// "Profil UNG": data scraped from ung.ac.id by NIP via the backend endpoint.
const ung = ref({ loading: false, data: null });

const fetchUng = async (nip) => {
  if (!nip) {
    ung.value = { loading: false, data: null };
    return;
  }
  ung.value = { loading: true, data: null };
  try {
    const res = await fetch(`/dosen-ung/${nip}`);
    if (res.ok) {
      const data = await res.json();
      ung.value = { loading: false, data: data && data.found ? data : null };
    } else {
      ung.value = { loading: false, data: null };
    }
  } catch (e) {
    ung.value = { loading: false, data: null };
  }
};

watch(
  () => [props.isOpen, resolvedDosen.value.nip],
  ([open, nip]) => {
    if (open && nip) fetchUng(nip);
    else if (!open) ung.value = { loading: false, data: null };
  },
  { immediate: true }
);
</script>

<style scoped>
/* Add responsive styling */
@media (max-width: 640px) {
  .max-w-2xl {
    width: 85%;
    /* Reduced from 95% to make modal medium sized on mobile */
    max-width: 450px;
    /* Set a max-width for medium size feel */
    margin: 0 auto;
  }

  /* Adjust padding for better spacing on mobile */
  .p-6 {
    padding: 1rem;
  }

  /* Keep image proportional but not too big */
  .max-w-\[200px\] {
    max-width: 150px;
  }
}

/* Add smooth transitions */
.fixed {
  transition: all 0.3s ease-in-out;
}

/* Center modal content on all screen sizes */
.flex.justify-center {
  align-items: center;
}
</style>
